import { useEffect, useMemo, useRef, useState } from "react";

import { DropdownWithCheckboxesItem } from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

interface InboxSendingUser {
	id: number;
}

interface UseInboxInitialSendersArgs {
	sendingUsers?: { data?: InboxSendingUser[] };
	userId?: number;
	userFirstName?: string;
	userLastName?: string;
}

/**
 * Resolves the Inbox's senders-dropdown selection: seeded from the URL on mount (defaulting to
 * "Me" when the current user is a sending user, else "All"), and re-derived once `sendingUsers`
 * loads if the URL didn't already pin a choice.
 */
export function useInboxInitialSenders({
	sendingUsers,
	userId,
	userFirstName,
	userLastName,
}: UseInboxInitialSendersArgs) {
	const doesSendingUsersHaveMe = useMemo(
		() => sendingUsers?.data?.some((sending) => sending.id === userId),
		[sendingUsers, userId]
	);

	const getSendersFromUrl = (): DropdownWithCheckboxesItem[] => {
		const params = new URLSearchParams(location.search);

		return JSON.parse(
			params.get("senders") ||
				JSON.stringify([
					{
						id: doesSendingUsersHaveMe ? userId : "all",
						title: doesSendingUsersHaveMe ? `Me (${userFirstName + " " + userLastName})` : `All`,
					},
				])
		);
	};

	const [selectedSenders, setSelectedSenders] =
		useState<DropdownWithCheckboxesItem[]>(getSendersFromUrl());

	const initialSenderParam = useRef(new URLSearchParams(location.search).get("senders"));

	useEffect(() => {
		if (initialSenderParam.current || !sendingUsers) {
			return;
		}

		const newId = doesSendingUsersHaveMe ? (userId ?? "all") : "all";
		const newTitle = doesSendingUsersHaveMe ? `Me (${userFirstName + " " + userLastName})` : "All";

		setSelectedSenders((prev) => {
			// Return the same reference if the value hasn't changed
			// to avoid re-triggering the getUnassigned effect
			if (prev.length === 1 && prev[0].id === newId && prev[0].title === newTitle) {
				return prev;
			}

			return [{ id: newId, title: newTitle }];
		});
	}, [sendingUsers, doesSendingUsersHaveMe]);

	return { doesSendingUsersHaveMe, selectedSenders, setSelectedSenders };
}
